import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useUserStore } from './user';
import { useRoute } from 'vue-router';

import dayjs, { Dayjs } from 'dayjs';
import { getAccountsList, getAccounts } from '@/utils/api';

import type { AccountType, AccountTypeSimple, AccountIndexQuery } from '@/types/Account.ts';
import { getParamsOnLoad } from '@/utils/storeMixin';

export const useAccountsStore = defineStore('accounts', () => {
  const userStore = useUserStore();
  const route = useRoute();

  const accountsDataSimple = ref<AccountTypeSimple[]>([]);
  const accountsData = ref<AccountType[]>([]);
  const lastPage = ref(0);
  const lastUpdated = ref<Dayjs>();
  const loading = ref(false);

  const getAccountDataSimple = async (override = false, ignoreLoad = false) => {
    // Skip the load, used for when we simply update
    // the list behind the scenes
    if (!ignoreLoad) {
      loading.value = true;
    }

    // Use the Store Cache if we have it.
    if (override === false && accountsDataSimple.value.length) {
      return accountsDataSimple.value;
    }

    return await getAccountsList(userStore.accessToken).then((resp) => {
      accountsDataSimple.value = resp;
      loading.value = false;
    });
  };

  const accountsDataSimpleWithAny = computed(() => {
    const emptyAccount: AccountTypeSimple = { id: -1, display_name: 'Any', sso_enabled: false };
    const withEmpty = [...accountsDataSimple.value];
    withEmpty.unshift(emptyAccount);
    return withEmpty;
  });

  getAccountDataSimple();

  const params = ref<AccountIndexQuery>({
    page: 1,
    results_per_page: 10,
    account_name: '',
    sortDesc: false,
    sortBy: 'display_name',
    include_inactive: false,
  });

  function getData(override = false, ignoreLoad = false) {
    // Skip the load, used for when we simply update
    // the list behind the scenes
    if (!ignoreLoad) {
      loading.value = true;
    }

    // Use the Store Cache if we have it.
    if (override === false && accountsData.value.length) {
      loading.value = false;
      return accountsData.value;
    }

    const newParams = { ...params.value };

    // TODO: Don't pass empty Sort param or it'll throw a server error.
    if (newParams.sortBy === '') {
      delete newParams.sortBy;
    }

    newParams.account_status = newParams.include_inactive ? [0] : [1];
    delete newParams.include_inactive;

    getAccounts(userStore.accessToken, newParams).then((response) => {
      const { data, last_page } = response;

      accountsData.value = data;
      lastPage.value = last_page;
      lastUpdated.value = dayjs();
      loading.value = false;
    });
  }

  function getQueryParamsOnLoad() {
    if (route.path === '/accounts') {
      params.value = getParamsOnLoad(params.value);
    }
  }

  const accountsDataRequired = computed(() => {
    return accountsDataSimple.value.filter((acct) => acct.id != null);
  });

  getQueryParamsOnLoad();

  return {
    accountsDataSimple,
    accountsDataSimpleWithAny,
    accountsData,
    accountsDataRequired,
    lastPage,
    params,
    loading,

    getAccountDataSimple,
    getData,
  };
});
